import React, { useState, useEffect } from 'react';
import { getOrderStatusHistory, updateOrderStatusWithHistory, getOrderStatuses } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  GitBranch, 
  ArrowRight, 
  Clock, 
  User,
  Plus,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

export default function OrderStatusHistory({ orderId, currentStatus, onStatusChange, readOnly = false }) {
  const [history, setHistory] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (orderId) {
      loadData();
    }
  }, [orderId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [historyRes, statusesRes] = await Promise.all([
        getOrderStatusHistory(orderId),
        getOrderStatuses()
      ]);
      setHistory(historyRes.data || []);
      setStatuses(statusesRes.data || []);
    } catch (error) {
      console.error('Failed to load status history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }

    setSaving(true);
    try {
      await updateOrderStatusWithHistory(orderId, newStatus, notes);
      toast.success('Status updated');
      setDialogOpen(false);
      setNewStatus('');
      setNotes('');
      loadData();
      if (onStatusChange) onStatusChange(newStatus);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (statusName) => {
    const status = statuses.find(s => s.name === statusName || s.code === statusName);
    return status?.color || '#6B7280';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="animate-spin w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="order-status-history">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <div className="flex items-center gap-3">
          <CardTitle className="text-lg">Status Timeline</CardTitle>
          <Badge 
            style={{ backgroundColor: getStatusColor(currentStatus), color: 'white' }}
          >
            {currentStatus}
          </Badge>
        </div>
        {!readOnly && (
          <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="change-status-btn">
            <Plus className="w-4 h-4 mr-1" /> Change Status
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {history.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <GitBranch className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No status changes recorded yet</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-slate-200" />
            
            <div className="space-y-4">
              {history.map((entry, index) => (
                <div key={entry.id || index} className="relative flex gap-4 pl-10" data-testid="status-history-entry">
                  {/* Timeline dot */}
                  <div 
                    className="absolute left-2 w-5 h-5 rounded-full border-2 border-white shadow-sm flex items-center justify-center"
                    style={{ backgroundColor: getStatusColor(entry.new_status) }}
                  >
                    {index === 0 ? (
                      <CheckCircle className="w-3 h-3 text-white" />
                    ) : null}
                  </div>
                  
                  <div className="flex-1 bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {entry.old_status && (
                        <>
                          <Badge variant="outline" className="text-xs">
                            {entry.old_status}
                          </Badge>
                          <ArrowRight className="w-3 h-3 text-slate-400" />
                        </>
                      )}
                      <Badge 
                        className="text-xs"
                        style={{ backgroundColor: getStatusColor(entry.new_status), color: 'white' }}
                      >
                        {entry.new_status}
                      </Badge>
                    </div>
                    
                    {entry.notes && (
                      <p className="text-sm text-slate-600 mt-2">{entry.notes}</p>
                    )}
                    
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {entry.changed_by_name || 'System'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(entry.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      {/* Change Status Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Order Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span>Current status:</span>
              <Badge 
                style={{ backgroundColor: getStatusColor(currentStatus), color: 'white' }}
              >
                {currentStatus}
              </Badge>
            </div>
            
            <div>
              <Label>New Status *</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="status-select">
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (
                    <SelectItem key={status.id} value={status.name}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: status.color }}
                        />
                        {status.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this status change..."
                rows={3}
                data-testid="status-notes"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateStatus} disabled={saving} data-testid="save-status-btn">
                {saving ? 'Saving...' : 'Update Status'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
